import { api } from "../api";
import { InterestContext } from "../context/auth";

const INTEREST_STORAGE_KEY = "user_interests";
const GUEST_INTEREST_KEY = "guest_interests";

const getInterestStorageKey = (userId: number) =>
  `${INTEREST_STORAGE_KEY}_${userId}`;

const syncInterestWithAPI = async (interest: InterestContext) => {
  try {
    await api.post("/web/shopping-cart", {
      product_id: interest.product.id,
      // Adicione outros campos necessários aqui
    });
  } catch (error) {
    console.error(`Erro ao sincronizar serviço ${interest.product.id}:`, error);
  }
};

const syncInterestsInBackground = (interests: InterestContext[]) => {
  // Usa Promise.allSettled para processar todos os serviços independentemente de erros
  Promise.allSettled(interests.map(syncInterestWithAPI))
    .then((results) => {
      const successCount = results.filter(
        (r) => r.status === "fulfilled"
      ).length;
      const errorCount = results.filter((r) => r.status === "rejected").length;
      // console.log(
      //   `Sincronização concluída: ${successCount} sucessos, ${errorCount} erros`
      // );
    })
    .catch((error) => {
      console.error("Erro geral na sincronização:", error);
    });
};

export const useInterestStorage = () => {
  const saveInterestsToStorage = (
    userId: number | null,
    interests: InterestContext[]
  ) => {
    try {
      if (userId) {
        const key = getInterestStorageKey(userId);
        localStorage.setItem(key, JSON.stringify(interests));
      } else {
        const key = GUEST_INTEREST_KEY;
        localStorage.setItem(key, JSON.stringify(interests));
      }
    } catch (error) {
      console.error("Erro ao salvar interesses:", error);
    }
  };

  const getInterestsFromStorage = (
    userId: number | null
  ): InterestContext[] => {
    try {
      if (userId) {
        const key = getInterestStorageKey(userId);
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
      } else {
        const key = GUEST_INTEREST_KEY;
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
      }
    } catch (error) {
      console.error("Erro ao recuperar interesses:", error);
      return [];
    }
  };

  const clearInterestsFromStorage = (userId: number) => {
    try {
      const key = getInterestStorageKey(userId);
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Erro ao limpar interesses:", error);
    }
  };

  const mergedInterests = (userId: number) => {
    try {
      if (!userId) {
        console.error("Erro: userId é necessário para mesclar interesses");
        return;
      }

      const guestInterests = getInterestsFromStorage(null);
      const userInterests = getInterestsFromStorage(userId);
      // Inicia sincronização em background
      syncInterestsInBackground(guestInterests);

      if (!guestInterests.length) {
        return userInterests;
      }

      if (!userInterests.length) {
        saveInterestsToStorage(userId, guestInterests);
        localStorage.removeItem(GUEST_INTEREST_KEY);

        return guestInterests;
      }

      const userProductMap = new Map(
        userInterests.map((interest) => [interest.product.id, interest])
      );

      const newInterests = guestInterests.filter(
        (guestInterest) => !userProductMap.has(guestInterest.product.id)
      );

      const mergedInterests = [...userInterests, ...newInterests];
      saveInterestsToStorage(userId, mergedInterests);
      localStorage.removeItem(GUEST_INTEREST_KEY);

      return mergedInterests;
    } catch (error) {
      console.error("Erro ao mesclar interesses:", error);
      return [];
    }
  };

  return {
    saveInterestsToStorage,
    getInterestsFromStorage,
    clearInterestsFromStorage,
    mergedInterests,
  };
};
